import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { LocalStorageService } from 'ngx-webstorage';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-directory-search-selection',
  templateUrl: './directory-search-selection.component.html',
  styleUrls: ['./directory-search-selection.component.scss']
})
export class DirectorySearchSelectionComponent implements OnInit
{
  @Input() public doNotResetScrollOnClose = false;
  @Output() public selectedDirectorySource = new EventEmitter<string>();

  public selectDirectorySourceDialogOpened = false;
  public sourceForm: any;
  public filteredDirectorySources: Array<{ text: string; id: string }>;

  private storageSourceDefault = 'provider-directory-source-default';

  private directorySources: Array<{ text: string; id: string }> = [
    { text: 'PROVIDER-DIRECTORY-SOURCES-SELECT', id: null },
    { text: 'PROVIDER-DIRECTORY-SOURCES-CHANNELS', id: 'CHANNELS' },
    { text: 'PROVIDER-DIRECTORY-SOURCES-KNO2', id: 'KNO2' }
  ];

  constructor(private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private settingService: SettingService,
    private storageService: LocalStorageService) { }

  public get directorySource(): UntypedFormControl
  {
    return this.sourceForm.get('directorySource');
  }

  public ngOnInit(): void
  {
    // Set the event types from localization
    this.translate.get([
      'PROVIDER-DIRECTORY-SOURCES-SELECT',
      'PROVIDER-DIRECTORY-SOURCES-CHANNELS',
      'PROVIDER-DIRECTORY-SOURCES-KNO2']).subscribe({
        next: (res) =>
        {
          let index = 0;
          for (const key in res)
          {
            if ({}.hasOwnProperty.call(res, key))
            {
              this.directorySources[index].text = res[key];
              index++;
            }
          }
        }
      });
  }

  public close(): void
  {
    this.selectDirectorySourceDialogOpened = false;
    if (!this.doNotResetScrollOnClose)
    {
      document.body.style.overflow = '';
    }
  }

  public execute(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.filteredDirectorySources = cloneDeep(this.directorySources);

        if (!sessionSetting.kno2Enabled)
        {
          this.filteredDirectorySources = this.filteredDirectorySources.filter(x => x.id !== 'KNO2');
        }

        if (!sessionSetting.providerEnabled)
        {
          this.filteredDirectorySources = this.filteredDirectorySources.filter(x => x.id !== 'CHANNELS');
        }

        // If only one directory, then no need to show dialog
        const sources = this.filteredDirectorySources.filter(x => x.id !== null);
        if (sources.length === 1)
        {
          this.selectedDirectorySource.emit(sources[sources.length - 1].id);
        }
        else
        {
          // Open the form to allow selecting a directory to search
          this.buildForm();
          this.selectDirectorySourceDialogOpened = true;
          document.body.style.overflow = 'hidden';
          this.setDefaultDirectorySource();
        }
      }
    });
  }

  /**
   * Directory Search Source selected
   *
   * @param e Event
   */
  public onSourceSelection(): void
  {
    // Save default selection to localstorage?
    this.storageService.store(`${this.storageSourceDefault}`, this.directorySource.value.id);
    this.selectDirectorySourceDialogOpened = false;
    this.selectedDirectorySource.emit(this.directorySource.value.id);
  }

  public keyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Enter' && this.sourceForm.valid)
    {
      event.preventDefault();
      this.onSourceSelection();
    }
  }

  /**
   * Create source form.
   */
  private buildForm(): void
  {
    // Set form
    this.sourceForm = this.fb.group({
      directorySource: [null, Validators.required]
    });
  }

  private setDefaultDirectorySource(): void
  {
    const defaultSource = this.storageService.retrieve(`${this.storageSourceDefault}`);
    if (defaultSource)
    {
      const defaultValue = this.filteredDirectorySources.filter(x => x.id === defaultSource);
      if (defaultValue && defaultValue.length === 1)
      {
        this.directorySource.setValue(defaultValue[0]);
        this.directorySource.updateValueAndValidity();
        this.sourceForm.markAsDirty();
      }
    }
  }
}
